import { Address } from "@redotech/account-common/address";
import type { TeamWith } from "@redotech/account-common/base-team";
import { Payer } from "@redotech/congo-enums/payer";
import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { findCountryName } from "@redotech/locale/countries";
import { PurchaseLabelType } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import {
  addWeight,
  Weight,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { t as defaultT, type Translate } from "@redotech/redo-i18n";
import AmazonIcon from "@redotech/redo-icons/carriers/amazon.svg";
import DHLIcon from "@redotech/redo-icons/carriers/dhl.svg";
import FedExIcon from "@redotech/redo-icons/carriers/fedex.svg";
import NoCarrierIcon from "@redotech/redo-icons/carriers/no-carrier.svg";
import UPSIcon from "@redotech/redo-icons/carriers/ups.svg";
import USPSIcon from "@redotech/redo-icons/carriers/usps.svg";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { OnboardingData } from "@redotech/redo-merchant-app-common/team";
import {
  MerchantAppReturn,
  productRequiresShipping,
  ReturnAddress,
  ReturnAddressToAddress,
  Shipment,
} from "@redotech/redo-return-core-common/return";
import { getIsFirstLabel } from "@redotech/redo-return-core-common/return-shipment-utils";
import { AddressAutocompleteFormValue } from "@redotech/redo-web/address-autocomplete-form";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import type { CreateLabelForm } from "./create-label-modal-forms";
import type {
  ConfirmationPageInfo,
  CreateLabelTarget,
} from "./create-label-modal-types";
import {
  AddressDropdownOption,
  AddressOption,
  LabelWeight,
  Page,
} from "./create-label-modal-types";

export const getCarrierLogo = (carrier: string) => {
  if (carrier.toLowerCase().includes("ups")) {
    return UPSIcon;
  } else if (carrier.toLowerCase().includes("fedex")) {
    return FedExIcon;
  } else if (carrier.toLowerCase().includes("usps")) {
    return USPSIcon;
  } else if (carrier.toLowerCase().includes("dhl")) {
    return DHLIcon;
  } else if (carrier.toLowerCase().includes("amazon")) {
    return AmazonIcon;
  } else {
    return NoCarrierIcon;
  }
};

export const getProductsWeight = ({
  products,
  team,
  onboardingData,
}: {
  products: {
    grams?: number;
    quantity: number;
    requiresShipping?: boolean | null;
  }[];
  team?: TeamWith<{ defaultLineItemOunces?: number }>;
  onboardingData?: OnboardingData;
}): LabelWeight => {
  const fallbackOunces =
    team?.settings.defaultLineItemOunces || onboardingData?.averageWeight || 16;
  const physicalProducts = products.filter(productRequiresShipping);
  let totalWeight: Weight = { value: 0, unit: WeightUnit.Lb };
  for (const product of physicalProducts) {
    let weight: Weight;
    if (product.grams) {
      weight = { value: product.grams, unit: WeightUnit.G };
    } else {
      weight = { value: fallbackOunces, unit: WeightUnit.Oz };
    }
    weight.value *= product.quantity;
    totalWeight = addWeight(totalWeight, weight);
  }
  const roundedWeight = Math.floor(totalWeight.value * 100) / 100;
  return { weight: roundedWeight, unit: totalWeight.unit };
};

interface IGetShipToAddressForCreateLabelProps {
  return_: MerchantAppReturn;
  shipmentId?: string;
  shipmentGroupId?: string;
}

function getShipToAddressForCreateLabel({
  return_,
  shipmentId,
  shipmentGroupId,
}: IGetShipToAddressForCreateLabelProps): ReturnAddress | undefined {
  // If a shipmentGroupId is provided, look up the group's address directly
  if (shipmentGroupId && return_.shipmentGroups?.length) {
    const group = return_.shipmentGroups.find((g) => g.id === shipmentGroupId);
    if (group?.address) {
      return group.address;
    }
  }

  if (
    shipmentId &&
    return_.shipmentGroups?.length &&
    return_.shipments?.length
  ) {
    const shipment = return_.shipments.find(
      (s) => (s as Shipment)._shipment?.id === shipmentId,
    );
    const groupId = shipment?.shipmentGroupID;
    const group = groupId
      ? return_.shipmentGroups.find((g) => g.id === groupId)
      : undefined;
    if (group?.address) {
      return group.address;
    }
  }

  return return_.merchant_address ?? undefined;
}

interface IGetDefaultCreateLabelValuesProps {
  return_: MerchantAppReturn;
  labelWeight: LabelWeight;
  shipmentId?: string;
  shipmentGroupId?: string;
}

export function getDefaultCreateLabelValues({
  return_,
  labelWeight,
  shipmentId,
  shipmentGroupId,
}: IGetDefaultCreateLabelValuesProps) {
  const shipToAddress = getShipToAddressForCreateLabel({
    return_,
    shipmentId,
    shipmentGroupId,
  });
  const merchantFormattedAddress = shipToAddress
    ? {
        ...convertReturnAddressToTeamAddress(shipToAddress),
        createdAt: null,
        updatedAt: null,
      }
    : undefined;
  const emptyAddressForm = addressToAddressForm(undefined);
  const shopperAddress = return_.shipping_address
    ? convertReturnAddressToTeamAddress(return_.shipping_address)
    : undefined;

  return {
    labelWeight,
    selectedShipping: undefined,
    customShipFromAddressForm: emptyAddressForm,
    customShipToAddressForm: emptyAddressForm,
    shopperFinalAddress: shopperAddress,
    shopperSelectedAddress: shopperAddress,
    merchantAddress: merchantFormattedAddress,
    merchantSelectedAddress: merchantFormattedAddress,
    customShipFromAddress: undefined,
    customShipToAddress: undefined,
    labelType: PurchaseLabelType.Replace,
    emailLabelToShopper: true,
    shopperEmail: "",
  };
}

export function isSamePhysicalAddress(a: Address, b: Address) {
  return a.street1 === b.street1 && a.city === b.city && a.zip === b.zip;
}

export function getShipAddressOptions(
  return_: MerchantAppReturn,
  locationAddresses: Address[] | undefined,
  shipmentId?: string,
  t: Translate = defaultT,
): RedoListItem<AddressOption>[] {
  const options: RedoListItem<AddressOption>[] = [];

  if (return_.shipping_address) {
    const convertedAddress = convertReturnAddressToTeamAddress(
      return_.shipping_address,
    );
    options.push({
      id: "customer-shipping-address",
      type: "text" as const,
      text:
        createAddressDisplayString(convertedAddress) || t("Customer's address"),
      value: { ...convertedAddress, createdAt: null, updatedAt: null },
    });
  }

  const shipToDefault = getShipToAddressForCreateLabel({ return_, shipmentId });
  if (shipToDefault) {
    const converted = convertReturnAddressToTeamAddress(shipToDefault);
    const alreadyExists = options.some(
      (o) => o.value !== "custom" && isSamePhysicalAddress(o.value, converted),
    );
    if (!alreadyExists) {
      options.push({
        id: "merchant-return-address",
        type: "text" as const,
        text: createAddressDisplayString(converted) || t("Return address"),
        value: { ...converted, createdAt: null, updatedAt: null },
      });
    }
  }

  if (locationAddresses) {
    const existingAddresses: Address[] = options
      .map((o) => o.value)
      .filter((v): v is Address => v !== "custom");
    for (const [index, address] of locationAddresses.entries()) {
      if (
        existingAddresses.some((seen) => isSamePhysicalAddress(seen, address))
      ) {
        continue;
      }
      existingAddresses.push(address);
      options.push({
        id: `location-${index}-${address.street1}`,
        type: "text" as const,
        text: createAddressDisplayString(address) || "",
        value: { ...address, createdAt: null, updatedAt: null },
      });
    }
  }

  options.push({
    id: "custom",
    type: "text" as const,
    text: t("Custom address..."),
    value: "custom" as const,
  });

  return options;
}

export function addressToAddressForm(
  address: Address | undefined,
): AddressAutocompleteFormValue {
  return {
    name: address?.name || "",
    address1: address?.street1 || "",
    address2: address?.street2 || "",
    city: address?.city || "",
    province: address?.state || "",
    zip: address?.zip || "",
    countryCode: address?.country as RedoShopifyCountryCode,
    phone: address?.phone || "",
    email: null,
    company: null,
  };
}

export function addressFormToReturnAddress(
  addressForm: AddressAutocompleteFormValue,
): Address {
  return {
    name: addressForm.name,
    street1: addressForm.address1,
    street2: addressForm.address2 || undefined,
    city: addressForm.city,
    state: addressForm.province || undefined,
    country: findCountryName(addressForm.countryCode) || "United States",
    zip: addressForm.zip,
    phone: addressForm.phone || undefined,
    email: addressForm.email || undefined,
  };
}

export function convertReturnAddressToTeamAddress(
  returnAddress: ReturnAddress,
): Address {
  const converted = ReturnAddressToAddress(returnAddress);
  return {
    ...converted,
    street2: converted.street2 || undefined,
    state: converted.state || undefined,
    phone: converted.phone || undefined,
  };
}

export function createAddressDisplayString(
  address: Address | undefined,
): string {
  if (!address) {
    return "";
  }

  const parts = [
    address.name,
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.zip,
  ].filter(Boolean);

  return parts.join(", ");
}

export function findSelectedAddressOption<T extends AddressOption>(
  options: AddressDropdownOption<T>[],
  selectedValue: T | undefined,
  customAddress?: Address,
): AddressDropdownOption<T> | undefined {
  if (!selectedValue) {
    return undefined;
  }

  if (selectedValue === "custom") {
    const customOption = options.find((option) => option.value === "custom");
    if (customOption && customAddress) {
      return {
        id: customOption.id,
        type: "text" as const,
        text: createAddressDisplayString(customAddress),
        value: customOption.value,
      };
    }
    return customOption;
  }

  return options.find(
    (option) =>
      option.value !== "custom" &&
      selectedValue !== "custom" &&
      isSamePhysicalAddress(option.value, selectedValue),
  );
}

export function calculateMerchantResponsibilityCost({
  labelPayer,
  labelType,
  selectedRatePrice,
  originalLabelCost,
  isFirstLabelOrReplacingFirstLabel,
  isFirstLabel,
  isMerchantCarrierAccount,
  redoMaxLabelPrice,
  labelDeductedFromCredit,
  isInStoreReturn,
}: {
  labelPayer?: Payer;
  labelType: PurchaseLabelType;
  selectedRatePrice: number;
  originalLabelCost: number;
  isFirstLabelOrReplacingFirstLabel: boolean;
  isFirstLabel: boolean;
  isMerchantCarrierAccount?: boolean;
  redoMaxLabelPrice: number;
  labelDeductedFromCredit?: boolean;
  isInStoreReturn?: boolean;
}): number {
  if (isMerchantCarrierAccount) {
    return 0;
  }

  if (!isFirstLabelOrReplacingFirstLabel) {
    if (labelType === PurchaseLabelType.Replace) {
      return Math.max(0, selectedRatePrice - originalLabelCost);
    }
    return selectedRatePrice;
  }

  if (labelPayer === Payer.Redo) {
    return Math.max(0, selectedRatePrice - redoMaxLabelPrice);
  }

  if (labelPayer === Payer.Customer) {
    if (labelDeductedFromCredit && isFirstLabel) {
      return selectedRatePrice;
    }
    return Math.max(0, selectedRatePrice - originalLabelCost);
  }

  // In-store returns carry no shipping coverage: merchant pays the full cost.
  if (labelPayer === Payer.Merchant || isInStoreReturn) {
    return Math.max(0, selectedRatePrice - originalLabelCost);
  }

  return 0;
}

// Coverage-applied merchant cost for any label/drop-off purchase, mirroring the
// backend: max(0, responsibility(newAmount) - replacedMerchantCost).
export function getReturnShippingMerchantCost({
  return_,
  newAmount,
  labelType,
  originalLabelCost,
  redoMaxLabelPrice,
  replacedMerchantCost = 0,
  replacedGroupId,
  isMerchantCarrierAccount = false,
}: {
  return_: MerchantAppReturn;
  newAmount: number;
  labelType: PurchaseLabelType;
  originalLabelCost: number;
  redoMaxLabelPrice: number;
  replacedMerchantCost?: number;
  replacedGroupId?: string;
  isMerchantCarrierAccount?: boolean;
}): number {
  const isFirstLabel = getIsFirstLabel(return_, replacedGroupId);
  const isFirstLabelOrReplacingFirstLabel =
    isFirstLabel ||
    (labelType === PurchaseLabelType.Replace &&
      (return_.shipments?.length ?? 0) === 1);

  const responsibility = calculateMerchantResponsibilityCost({
    labelPayer: return_.labelPayer ?? undefined,
    labelType,
    selectedRatePrice: newAmount,
    originalLabelCost,
    isFirstLabelOrReplacingFirstLabel,
    isFirstLabel,
    isMerchantCarrierAccount,
    redoMaxLabelPrice,
    labelDeductedFromCredit: return_.labelDeductedFromCredit ?? false,
    isInStoreReturn: return_.inStoreReturn ?? false,
  });

  return Math.max(
    0,
    Math.round((responsibility - replacedMerchantCost) * 100) / 100,
  );
}

export function getReturnShippingPriceDescription({
  noun,
  labelPayer,
  originalLabelCost,
  formatCurrency,
  isFirstLabelOrReplacingFirstLabel,
  isFirstLabel,
  isMerchantCarrierAccount,
  redoMaxLabelPrice,
  labelDeductedFromCredit,
  translate: t = defaultT,
}: {
  noun: "label" | "drop-off";
  labelPayer?: Payer;
  originalLabelCost?: number;
  formatCurrency: (value: number) => string;
  isFirstLabelOrReplacingFirstLabel: boolean;
  isFirstLabel: boolean;
  isMerchantCarrierAccount?: boolean;
  redoMaxLabelPrice: number;
  labelDeductedFromCredit?: boolean;
  translate?: Translate;
}): string {
  if (isMerchantCarrierAccount) {
    return t("Using the merchant's carrier account - no charge from Redo.");
  }

  if (!isFirstLabelOrReplacingFirstLabel) {
    return noun === "label"
      ? t("The cost of additional labels is the merchant's responsibility.")
      : t("The cost of additional drop-offs is the merchant's responsibility.");
  }

  if (labelPayer === Payer.Redo) {
    return noun === "label"
      ? t(
          "Redo is responsible for up to {{amount}} of the label cost. Any costs exceeding this amount are the merchant's responsibility.",
          { amount: formatCurrency(redoMaxLabelPrice) },
        )
      : t(
          "Redo is responsible for up to {{amount}} of the drop-off cost. Any costs exceeding this amount are the merchant's responsibility.",
          { amount: formatCurrency(redoMaxLabelPrice) },
        );
  }

  if (labelPayer === Payer.Customer) {
    if (labelDeductedFromCredit && isFirstLabel) {
      return noun === "label"
        ? t(
            "The original label cost was deducted from the customer's refund but no label was generated. The merchant is responsible for the full cost of this label.",
          )
        : t(
            "The original drop-off cost was deducted from the customer's refund but no drop-off was created. The merchant is responsible for the full cost of this drop-off.",
          );
    }
    if (originalLabelCost != null && originalLabelCost > 0) {
      return t(
        "The customer has already paid {{amount}}. Any costs exceeding this amount are the merchant's responsibility.",
        { amount: formatCurrency(originalLabelCost) },
      );
    }
    return noun === "label"
      ? t("The merchant is responsible for the label cost.")
      : t("The merchant is responsible for the drop-off cost.");
  }

  if (labelPayer === Payer.Merchant) {
    return noun === "label"
      ? t("The merchant is responsible for the label cost.")
      : t("The merchant is responsible for the drop-off cost.");
  }

  return noun === "label"
    ? t(
        "The merchant may be responsible for label costs. Please contact support for exact details about this return's label cost responsibility.",
      )
    : t(
        "The merchant may be responsible for drop-off costs. Please contact support for exact details about this return's drop-off cost responsibility.",
      );
}

export function getCreateLabelModalTitle(
  labelType: PurchaseLabelType,
  t: Translate = defaultT,
): string {
  return labelType === PurchaseLabelType.New ? t("Add label") : t("Edit label");
}

export function getCreateLabelModalSubtitle(
  labelType: PurchaseLabelType,
  t: Translate = defaultT,
): string {
  return labelType === PurchaseLabelType.New
    ? t("Create an additional label for this return")
    : t("Void existing label and replace it with a new one");
}

export function getConfirmationPageTitle(
  confirmationPageInfo: ConfirmationPageInfo | undefined,
  t: Translate = defaultT,
): string {
  if (!confirmationPageInfo?.success) {
    return t("Failed to create return shipping");
  }
  return confirmationPageInfo.kind === "dropoff"
    ? t("Drop-off created successfully")
    : t("Label created successfully");
}

export function getConfirmationPrimaryButtonText(
  confirmationPageInfo: ConfirmationPageInfo | undefined,
  t: Translate = defaultT,
): string {
  if (confirmationPageInfo?.success) {
    if (confirmationPageInfo.kind === "dropoff") {
      return confirmationPageInfo.qrCodeUrl
        ? t("Download QR code")
        : t("Close");
    }
    return confirmationPageInfo.labelUrl ? t("Download label") : t("Close");
  }
  return t("Go back");
}

export function applyCustomShipFromAddress(
  createLabelInput: CreateLabelForm,
  setPage: (page: Page) => void,
): void {
  const addressForm = createLabelInput.inputs.customShipFromAddressForm.value;
  const customAddress = addressFormToReturnAddress(addressForm);
  createLabelInput.inputs.customShipFromAddress.setValue(customAddress);
  createLabelInput.inputs.shopperFinalAddress.setValue(customAddress);
  createLabelInput.inputs.shopperSelectedAddress.setValue("custom");
  setPage(Page.CREATE_LABEL);
}

export function applyCustomShipToAddress(
  createLabelInput: CreateLabelForm,
  setPage: (page: Page) => void,
): void {
  const addressForm = createLabelInput.inputs.customShipToAddressForm.value;
  const customAddress: Address = {
    name: addressForm.name || "",
    street1: addressForm.address1,
    street2: addressForm.address2 || "",
    city: addressForm.city,
    state: addressForm.province || undefined,
    country: addressForm.countryCode,
    zip: addressForm.zip,
    phone: addressForm.phone || "",
    email: addressForm.email || undefined,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  createLabelInput.inputs.customShipToAddress.setValue(customAddress);
  createLabelInput.inputs.merchantSelectedAddress.setValue("custom");
  // A custom ship-to may not serve the chosen drop-off provider; clear it.
  if (createLabelInput.inputs.selectedShipping.value?.method === "dropoff") {
    createLabelInput.inputs.selectedShipping.setValue(undefined);
  }
  setPage(Page.CREATE_LABEL);
}

export function getCreateLabelModalTitleByPage(
  page: Page,
  labelType: PurchaseLabelType,
  confirmationPageInfo: ConfirmationPageInfo | undefined,
  t: Translate = defaultT,
): string {
  switch (page) {
    case Page.CREATE_LABEL:
      return getCreateLabelModalTitle(labelType, t);
    case Page.CUSTOM_SHIP_FROM:
      return t("Custom ship from address");
    case Page.CUSTOM_SHIP_TO:
      return t("Custom ship to address");
    case Page.CONFIRMATION:
      return getConfirmationPageTitle(confirmationPageInfo, t);
    case Page.DROPOFF_LOCATIONS:
      return t("Drop-off locations");
  }
}

export function getCreateLabelModalSubtitleByPage(
  page: Page,
  labelType: PurchaseLabelType,
  t: Translate = defaultT,
): string | undefined {
  return page === Page.CREATE_LABEL
    ? getCreateLabelModalSubtitle(labelType, t)
    : undefined;
}

export function getCreateLabelPrimaryButtonTextByPage(
  page: Page,
  formattedCost: string | undefined,
  confirmationPageInfo: ConfirmationPageInfo | undefined,
  t: Translate = defaultT,
): string {
  switch (page) {
    case Page.CREATE_LABEL:
      return formattedCost
        ? t("Create label ({{cost}})", { cost: formattedCost }) ||
            `Create label (${formattedCost})`
        : t("Create label") || "Create label";
    case Page.CUSTOM_SHIP_FROM:
    case Page.CUSTOM_SHIP_TO:
      return t("Save") || "Save";
    case Page.CONFIRMATION:
      return getConfirmationPrimaryButtonText(confirmationPageInfo, t);
    case Page.DROPOFF_LOCATIONS:
      return t("Back");
  }
}

export function getCreateLabelSecondaryButtonTextByPage(
  page: Page,
  confirmationPageInfo: ConfirmationPageInfo | undefined,
  t: Translate = defaultT,
): string | undefined {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (page) {
    case Page.CREATE_LABEL:
    case Page.CUSTOM_SHIP_FROM:
    case Page.CUSTOM_SHIP_TO:
      return t("Cancel");
    case Page.CONFIRMATION:
      return confirmationPageInfo?.success &&
        confirmationPageInfo.kind === "label" &&
        confirmationPageInfo.qrCodeUrl
        ? t("Download QR code")
        : undefined;
    default:
      return undefined;
  }
}

export function getCreateLabelPrimaryButtonDisabledByPage(
  page: Page,
  ratesLoading: boolean,
  hasSelectedLabel: boolean,
): boolean | undefined {
  return page === Page.CREATE_LABEL
    ? ratesLoading || !hasSelectedLabel
    : undefined;
}

export function resolveCreateLabelTarget(target: CreateLabelTarget): {
  labelType: PurchaseLabelType;
  shipmentId?: string;
  shipmentGroupId?: string;
  isAdditionalShipment?: boolean;
  editingDropoffGroupId?: string;
} {
  switch (target.type) {
    case "replace":
      return {
        labelType: PurchaseLabelType.Replace,
        shipmentId: target.shipmentId,
        isAdditionalShipment: target.isAdditionalShipment,
      };
    case "addToGroup":
      return {
        labelType: PurchaseLabelType.New,
        shipmentGroupId: target.shipmentGroupId,
      };
    case "add":
      return {
        labelType: PurchaseLabelType.New,
        shipmentId: target.fromShipmentId,
        isAdditionalShipment: target.isAdditionalShipment,
      };
    case "editDropoff":
      return {
        labelType: PurchaseLabelType.New,
        editingDropoffGroupId: target.shipmentGroupId,
      };
  }
}

export function createLabelTargetKey(
  target: CreateLabelTarget,
): string | undefined {
  switch (target.type) {
    case "replace":
      return target.shipmentId;
    case "addToGroup":
      return target.shipmentGroupId;
    case "add":
      return target.fromShipmentId;
    case "editDropoff":
      return target.shipmentGroupId;
  }
}
